import {
  BackupPolicyType,
  ScheduleType,
  type BackupSchedule,
} from '@repo/api-gw-sdk';
import { useCallback, useMemo } from 'react';

import { menuItemStyles } from '@/components/option-menu/option-menu';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';

import {
  annuallyFrequency,
  dailyFrequency,
  fourTimesADayFrequency,
  type Frequency,
  highFrequencyOptions,
  hourlyFrequency,
  monthlyFrequency,
  pitrFrequencyOptions,
  threeTimesADayFrequency,
  twiceADayFrequency,
  weeklyFrequency,
} from '../frequency';

const frequencyOptions: Frequency[] = [
  dailyFrequency,
  weeklyFrequency,
  monthlyFrequency,
  annuallyFrequency,
];

const standardMoreOptions: Frequency[] = [
  twiceADayFrequency,
  threeTimesADayFrequency,
  fourTimesADayFrequency,
];

export const useFrequencyOptions = (
  frequency: Frequency | undefined,
  policyType: BackupPolicyType,
  schedule: BackupSchedule
) => {
  const { enableHourlyBackup } = useFeatureFlags();
  const convertToOptionType = useCallback(
    (f: Frequency) => ({
      id: f.windows,
      text: f.text.toLowerCase(),
      menuItemProps: {
        sx: menuItemStyles(f === frequency),
      },
    }),
    [frequency]
  );

  return useMemo(() => {
    const moreFrequencyOptions = [
      {
        divider: true,
        id: 'create-backup-policy-frequency-divider',
      },
      {
        text: 'More frequently',
        id: 'create-backup-policy-high-frequency',
        subMenu: (enableHourlyBackup
          ? [...standardMoreOptions, hourlyFrequency]
          : standardMoreOptions
        ).map(convertToOptionType),
      },
    ];

    const pitrMainScheduleOptions = [
      ...[dailyFrequency].map(convertToOptionType),
      ...moreFrequencyOptions,
    ];

    const pitrScheduleOptions = pitrFrequencyOptions.map(convertToOptionType);

    const highFrequencyScheduleOptions =
      highFrequencyOptions.map(convertToOptionType);

    const standardOptions = [
      ...frequencyOptions.map(convertToOptionType),
      ...moreFrequencyOptions,
    ];

    return policyType === BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY
      ? highFrequencyScheduleOptions
      : policyType === BackupPolicyType.BACKUP_POLICY_TYPE_PITR
        ? schedule.scheduleType === ScheduleType.SCHEDULE_TYPE_PITR
          ? pitrScheduleOptions
          : pitrMainScheduleOptions
        : standardOptions;
  }, [
    enableHourlyBackup,
    convertToOptionType,
    policyType,
    schedule.scheduleType,
  ]);
};
